import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import {
  CheckCircle2,
  Copy,
  Eye,
  EyeOff,
  LocateFixed,
  MoreHorizontal,
  Pencil,
  RotateCcw,
  Send,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "react-toastify";

import { useSelector } from "react-redux";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "components/common/dropdown/DropDownV2";
import { cn } from "utils/cn";
import { Button } from "components/common/buttons/Button";
import { QueryLoadError } from "components/common/QueryLoadError";
import {
  formatFullTimestampForTooltip,
  formatRelativeActivityDate,
  parseInstantFromApi,
} from "utils/dateFormatting";
import {
  useClearCommentThreadRead,
  useCommentThreadReads,
  useMarkCommentThreadRead,
} from "../../hooks/comments";
import { useAutosizeTextarea } from "../../hooks/use-autosize-textarea";
import { ScriptCommentsPanelFilterMenu } from "./ScriptCommentsPanelFilterMenu";
import { ScriptCommentsPanelSortMenu } from "./ScriptCommentsPanelSortMenu";
import { DEFAULT_COMMENT_PANEL_SORT } from "./commentPanelSort.types";
import type { CommentPanelSort } from "./commentPanelSort.types";
import { sortCommentThreads } from "./commentPanelSort";
import { commentActionVisibility } from "./commentActionVisibility";
import { commentAuthorLabel } from "./commentAuthorLabel";
import { usePreviousVersionCommentsLabel } from "./previousVersionComments";
import {
  CLEAR_COMMENT_PANEL_FILTERS,
  DEFAULT_COMMENT_PANEL_FILTERS,
  commentPanelEmptyStateMessage,
  effectiveCommentPanelFilters,
  filterCommentThreads,
} from "./scriptCommentsPanelFilters";
import type { CommentPanelFilters } from "./scriptCommentsPanelFilters.types";
import { isThreadUnreadByReadAt } from "../../script-comment-thread-reads";
import { paginateScreenplayParagraphs } from "../../utils";
import { DocumentComment, DraftScene, DraftSceneSummary } from "../../types";
import type { AppState } from "store/reducers/Reducer";
import { useSessionUser } from "hooks/useSession";

function profileInitials(firstName: string, lastName: string, email: string): string {
  const f = firstName.trim();
  const l = lastName.trim();
  if (f && l) return `${f[0]!}${l[0]!}`.toUpperCase();
  if (f.length >= 2) return f.slice(0, 2).toUpperCase();
  if (f.length === 1) return `${f[0]!}${f[0]!}`.toUpperCase();
  const local = email.split("@")[0] ?? "";
  const cleaned = local.replace(/[^a-zA-Z0-9]/g, "");
  if (cleaned.length >= 2) return cleaned.slice(0, 2).toUpperCase();
  if (cleaned.length === 1) return `${cleaned[0]!}${cleaned[0]!}`.toUpperCase();
  return "?";
}

function hueFromUserId(userId: string): number {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash) % 360;
}

/** A person's own avatar color, derived from their id: identity data, not a theme color. */
function personHueColor(hue: number): string {
  return `hsl(${hue} 26% 32%)`;
}

function getAvatarVisual(
  userId: string | null | undefined,
  currentUserId: string | undefined,
  myInitials: string
): { initials: string; style: CSSProperties } {
  if (!userId) {
    return { initials: "?", style: { backgroundColor: "rgb(var(--muted))" } };
  }
  const hue = hueFromUserId(userId);
  if (currentUserId && userId === currentUserId) {
    const initials = (myInitials || "?").slice(0, 2).toUpperCase();
    return {
      initials,
      style: {
        backgroundImage:
          "linear-gradient(145deg, rgb(var(--hue-sky) / 0.45), rgb(var(--hue-blue) / 0.45))",
      },
    };
  }
  return {
    initials: "?",
    style: { backgroundColor: personHueColor(hue) },
  };
}

function isCommentOwner(
  createdByUserId: string | null | undefined,
  currentUserId: string | undefined
): boolean {
  return Boolean(currentUserId && createdByUserId && createdByUserId === currentUserId);
}

function scenePageWithinLabel(
  scene: DraftScene | undefined,
  anchorParagraphId: number | null | undefined
): string | null {
  if (!scene?.paragraphs) return null;
  const pages = paginateScreenplayParagraphs(scene.paragraphs);
  let within = 1;
  if (anchorParagraphId != null) {
    const match = pages.find((pg) =>
      pg.paragraphs.some((p) => p.paragraph_id === anchorParagraphId)
    );
    if (match) within = match.pageNumber;
  }
  return `Page ${within}`;
}

function truncateHeading(text: string, max: number): string {
  const t = text.replace(/\s+/g, " ").trim();
  if (t.length <= max) return t;
  return `${t.slice(0, Math.max(0, max - 1))}…`;
}

function ThreadMetaParts({
  sceneSummaries,
  activeScene,
  anchorSceneId,
  threadIndex,
  anchorParagraphId,
}: {
  sceneSummaries?: DraftSceneSummary[];
  activeScene?: DraftScene;
  anchorSceneId?: number | null;
  threadIndex: number;
  anchorParagraphId: number | null | undefined;
}) {
  const summary =
    anchorSceneId != null && Number.isFinite(anchorSceneId)
      ? sceneSummaries?.find((s) => s.scene_id === anchorSceneId)
      : undefined;
  const sceneLabel = summary
    ? truncateHeading(`${summary.sequence}. ${summary.scene_heading}`, 44)
    : null;

  const pageLabel =
    activeScene &&
    anchorSceneId != null &&
    activeScene.scene_id === anchorSceneId &&
    anchorParagraphId != null
      ? scenePageWithinLabel(activeScene, anchorParagraphId)
      : null;

  return (
    <span className="inline-flex min-w-0 items-baseline gap-1 whitespace-nowrap tabular-nums text-muted-foreground/70">
      <span className="font-normal">#{threadIndex}</span>
      {sceneLabel ? (
        <span
          className="max-w-[11rem] truncate text-muted-foreground/70"
          title={summary?.scene_heading}
        >
          {sceneLabel}
        </span>
      ) : null}
      {pageLabel ? <span className="text-muted-foreground/70">{pageLabel}</span> : null}
    </span>
  );
}

function truncatePreview(text: string, max: number): string {
  const t = text.replace(/\s+/g, " ").trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

function uniqueParticipantIds(root: DocumentComment, replies: DocumentComment[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const push = (id: string | null | undefined) => {
    if (id == null || seen.has(id)) return;
    seen.add(id);
    out.push(id);
  };
  push(root.created_by_user_id);
  for (const r of replies) {
    push(r.created_by_user_id);
    if (out.length >= 3) break;
  }
  return out;
}

function SmallAvatar({
  userId,
  className,
  style: ringStyle,
  currentUserId,
  myInitials,
}: {
  userId: string | null | undefined;
  className?: string;
  style?: CSSProperties;
  currentUserId?: string;
  myInitials: string;
}) {
  const { initials, style } = getAvatarVisual(userId, currentUserId, myInitials);
  return (
    <div
      className={cn(
        "flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border text-[9px] font-semibold tracking-tight text-foreground shadow-sm",
        className
      )}
      style={{ ...style, ...ringStyle }}
      aria-hidden
    >
      {initials}
    </div>
  );
}

function AvatarStack({
  userIds,
  currentUserId,
  myInitials,
}: {
  userIds: string[];
  currentUserId?: string;
  myInitials: string;
}) {
  if (userIds.length === 0) {
    return <SmallAvatar userId={null} currentUserId={currentUserId} myInitials={myInitials} />;
  }
  return (
    <div className="flex items-center">
      {userIds.slice(0, 3).map((id, i) => (
        <SmallAvatar
          key={`${id}-${i}`}
          userId={id}
          currentUserId={currentUserId}
          myInitials={myInitials}
          className={cn(i > 0 && "-ml-1.5")}
        />
      ))}
    </div>
  );
}

function ThreadBodyClamp({
  body,
  anchorText,
  variant = "root",
  compactSignal = 0,
}: {
  body: string;
  anchorText: string | null | undefined;
  /** `reply`: compact body text, no anchor block (anchor ignored). */
  variant?: "root" | "reply";
  /** Increment (e.g. on panel deselect) to collapse “See more” back to clamped. */
  compactSignal?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [needsToggle, setNeedsToggle] = useState(false);
  const isReply = variant === "reply";

  useEffect(() => {
    setExpanded(false);
  }, [compactSignal]);

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    if (expanded) {
      setNeedsToggle(true);
      return;
    }
    const measure = () => {
      const scrollable = el.scrollHeight > el.clientHeight + 1;
      setNeedsToggle(scrollable);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [body, anchorText, expanded, isReply, compactSignal]);

  return (
    <div className="mt-0">
      <div
        ref={contentRef}
        className={cn(
          !expanded && "line-clamp-4",
          isReply
            ? "text-[12px] leading-snug text-foreground/80"
            : "text-[14px] leading-[1.4] text-muted-foreground/50"
        )}
      >
        <p className="whitespace-pre-wrap">{body}</p>
        {!isReply && anchorText ? (
          <p className="mt-1.5 rounded-sm border-l-2 border-border bg-muted/50 py-1 pl-1.5 pr-1 text-[11px] italic leading-snug text-muted-foreground/70">
            &quot;{anchorText}&quot;
          </p>
        ) : null}
      </div>
      {needsToggle ? (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          className="mt-1 text-[11px] font-medium text-hue-sky transition hover:text-hue-sky/80"
        >
          {expanded ? "See less" : "See more"}
        </button>
      ) : null}
    </div>
  );
}

export type CommentThreadScrollRequest = {
  rootCommentId: string;
  nonce: number;
};

export type ScriptCommentsPanelProps = {
  projectId: string;
  documentId: string;
  /** Loaded draft for the active scene; used for page-within-scene when the thread anchor matches. */
  activeScene?: DraftScene;
  sceneSummaries?: DraftSceneSummary[];
  comments: DocumentComment[];
  isCreatingComment: boolean;
  isResolvingComment: boolean;
  isReopeningComment?: boolean;
  readOnly?: boolean;
  canComment: boolean;
  canResolve: boolean;
  /** `project:comment:moderate`: can delete other people's comments (edit stays author-only). */
  canModerate?: boolean;
  /** The comment list request failed; show an error instead of "no comments". */
  listLoadFailed?: boolean;
  onRetryListLoad?: () => void;
  /** Scope label for empty states (default screenplay). */
  commentsScope?: "screenplay" | "schedule" | "project";
  onCreateReply: (commentId: string, body: string) => Promise<void>;
  onResolveComment: (commentId: string) => Promise<void>;
  onReopenComment: (commentId: string) => Promise<void>;
  /** When set, scrolls the matching root thread into view (e.g. gutter icon click). */
  scrollToThreadRequest?: CommentThreadScrollRequest | null;
  onScrollToThreadHandled?: () => void;
  /** Scroll/focus the screenplay paragraph for this thread’s anchor (optional scene switch). */
  onGoToAnchor?: (paragraphId: number, sceneId?: number | null) => void;
  /** Scroll to a schedule stripboard anchor by target_id. */
  onGoToScheduleAnchor?: (targetId: string) => void;
  /** When set, footer composes a new root comment on this anchor. */
  pendingRootAnchor?: { anchorText: string } | null;
  onCreateRootComment?: (body: string) => Promise<void>;
  onClearPendingRootAnchor?: () => void;
  onUpdateComment: (commentId: string, body: string) => Promise<void>;
  onDeleteComment: (commentId: string) => void;
  isUpdatingComment: boolean;
  isDeletingComment: boolean;
};

export const ScriptCommentsPanel = ({
  projectId,
  documentId,
  activeScene,
  sceneSummaries = [],
  comments,
  isCreatingComment,
  isResolvingComment,
  isReopeningComment = false,
  readOnly = false,
  canComment,
  canResolve,
  commentsScope = "screenplay",
  onCreateReply,
  onResolveComment,
  onReopenComment,
  scrollToThreadRequest,
  onScrollToThreadHandled,
  onGoToAnchor,
  onGoToScheduleAnchor,
  pendingRootAnchor = null,
  onCreateRootComment,
  onClearPendingRootAnchor,
  onUpdateComment,
  onDeleteComment,
  isUpdatingComment,
  isDeletingComment,
  canModerate = false,
  listLoadFailed = false,
  onRetryListLoad,
}: ScriptCommentsPanelProps) => {
  const replyFooterRef = useRef<HTMLDivElement>(null);
  const currentUserId = useSelector((s: AppState) => s.auth.user_id);
  const authEmail = useSelector((s: AppState) => s.auth.email);
  const { session } = useSessionUser();
  const firstName = session?.first_name;
  const lastName = session?.last_name;
  const profileEmail = session?.email;

  const commentsReadsEnabled = Boolean(projectId && documentId);
  const threadReadsQuery = useCommentThreadReads(projectId, documentId, commentsReadsEnabled);
  const markCommentThreadRead = useMarkCommentThreadRead();
  const clearCommentThreadRead = useClearCommentThreadRead();

  const myInitials = useMemo(
    () => profileInitials(firstName ?? "", lastName ?? "", profileEmail || authEmail || ""),
    [firstName, lastName, profileEmail, authEmail]
  );

  const [replyTargetRootId, setReplyTargetRootId] = useState<string | null>(null);
  const [replyDraft, setReplyDraft] = useState("");
  const [rootDraft, setRootDraft] = useState("");
  /** Bumped when reply selection clears so bodies collapse (“See more”) and reply lists fold. */
  const [bodyCompactSignal, setBodyCompactSignal] = useState(0);
  const prevReplyTargetRef = useRef<string | null>(null);
  /** Same cap as Tailwind `max-h-24` (6rem at default root). */
  const replyComposerMaxHeightPx = 96;
  const replyComposerTextareaClassName = cn(
    "max-h-24 min-h-[44px] resize-none overflow-x-hidden rounded-xl border border-border bg-background px-3 py-2.5 text-sm leading-snug text-foreground outline-none ring-offset-editor-canvas placeholder:text-muted-foreground/70 focus-visible:border-border focus-visible:ring-2 focus-visible:ring-ring/30 disabled:cursor-not-allowed disabled:opacity-45"
  );
  const assignReplyAutosizeRef = useAutosizeTextarea(
    replyDraft,
    1,
    undefined,
    false,
    undefined,
    replyComposerMaxHeightPx
  );
  const [commentFilters, setCommentFilters] = useState<CommentPanelFilters>(
    DEFAULT_COMMENT_PANEL_FILTERS
  );
  const [commentSort, setCommentSort] = useState<CommentPanelSort>(DEFAULT_COMMENT_PANEL_SORT);
  const [repliesExpanded, setRepliesExpanded] = useState<Record<string, boolean>>({});
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const assignEditAutosizeRef = useAutosizeTextarea(
    editDraft,
    1,
    undefined,
    false,
    undefined,
    replyComposerMaxHeightPx
  );

  // Without read times, every thread would look never read. Until the first load succeeds the
  // read state is unknown: no unread dots, read toggles, read filters or unread-first ranking.
  // A failed refetch keeps the read times that loaded.
  const readAtByRoot = threadReadsQuery.data ?? null;
  const readTimesKnown = readAtByRoot !== null;
  const readStateLoadFailed = threadReadsQuery.isError && !readTimesKnown;
  const appliedFilters = useMemo(
    () => effectiveCommentPanelFilters(commentFilters, readTimesKnown),
    [commentFilters, readTimesKnown]
  );
  const readFilterPaused = appliedFilters.read !== commentFilters.read;
  const previousVersionComments = usePreviousVersionCommentsLabel(projectId, documentId);

  const markThreadReadNow = useCallback(
    (rootCommentId: string) => {
      markCommentThreadRead.mutate({ projectId, documentId, rootCommentId });
    },
    [documentId, markCommentThreadRead, projectId]
  );

  /** Selecting a thread marks it read (server `read_at`); re-clicking the same row does not (so “Mark as unread” sticks). */
  const engageThread = useCallback(
    (rootCommentId: string) => {
      if (replyTargetRootId !== rootCommentId) {
        markThreadReadNow(rootCommentId);
      }
      setReplyTargetRootId(rootCommentId);
    },
    [markThreadReadNow, replyTargetRootId]
  );

  useEffect(() => {
    if (!scrollToThreadRequest || !onScrollToThreadHandled) return;
    const { rootCommentId } = scrollToThreadRequest;
    let cancelled = false;
    const timers: number[] = [];

    const schedule = (fn: () => void, ms: number) => {
      timers.push(window.setTimeout(fn, ms));
    };

    schedule(() => {
      if (cancelled) return;
      const el = document.querySelector<HTMLElement>(
        `[data-comment-thread-root-id="${CSS.escape(rootCommentId)}"]`
      );
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "nearest" });
        engageThread(rootCommentId);
        setRepliesExpanded((prev) => ({ ...prev, [rootCommentId]: true }));
        onScrollToThreadHandled();
        return;
      }
      setCommentFilters(CLEAR_COMMENT_PANEL_FILTERS);
      schedule(() => {
        if (cancelled) return;
        const retry = document.querySelector<HTMLElement>(
          `[data-comment-thread-root-id="${CSS.escape(rootCommentId)}"]`
        );
        if (retry) {
          retry.scrollIntoView({ behavior: "smooth", block: "nearest" });
          engageThread(rootCommentId);
          setRepliesExpanded((prev) => ({ ...prev, [rootCommentId]: true }));
        }
        onScrollToThreadHandled();
      }, 240);
    }, 120);

    return () => {
      cancelled = true;
      for (const id of timers) {
        window.clearTimeout(id);
      }
    };
  }, [scrollToThreadRequest, onScrollToThreadHandled, engageThread]);

  useEffect(() => {
    if (!replyTargetRootId) return;
    const rootStillPresent = comments.some(
      (c) => c.comment_id === replyTargetRootId && !c.parent_comment_id
    );
    if (!rootStillPresent) {
      setReplyTargetRootId(null);
    }
  }, [comments, replyTargetRootId]);

  useEffect(() => {
    if (!editingCommentId) return;
    const exists = comments.some((c) => c.comment_id === editingCommentId);
    if (!exists) {
      setEditingCommentId(null);
      setEditDraft("");
    }
  }, [comments, editingCommentId]);

  const commentMutationBusy = isUpdatingComment || isDeletingComment;

  const commentThreads = useMemo(() => {
    const repliesByParent = new Map<string, DocumentComment[]>();
    const roots: DocumentComment[] = [];

    for (const comment of comments) {
      if (comment.parent_comment_id) {
        const replies = repliesByParent.get(comment.parent_comment_id) ?? [];
        replies.push(comment);
        repliesByParent.set(comment.parent_comment_id, replies);
      } else {
        roots.push(comment);
      }
    }

    return sortCommentThreads(
      roots.map((comment) => ({
        comment,
        replies: repliesByParent.get(comment.comment_id) ?? [],
      })),
      commentSort,
      { readAtByRoot, currentUserId }
    );
  }, [commentSort, comments, currentUserId, readAtByRoot]);

  const visibleThreads = useMemo(
    () =>
      filterCommentThreads(commentThreads, appliedFilters, {
        readAtByRoot,
        currentUserId,
        replyTargetRootId,
      }),
    [appliedFilters, commentThreads, currentUserId, readAtByRoot, replyTargetRootId]
  );

  useEffect(() => {
    if (!replyTargetRootId) return;
    const stillVisible = visibleThreads.some(
      ({ comment }) => comment.comment_id === replyTargetRootId
    );
    if (!stillVisible) {
      setReplyTargetRootId(null);
    }
  }, [visibleThreads, replyTargetRootId]);

  useEffect(() => {
    if (!replyTargetRootId) return;
    setRepliesExpanded((prev) => ({ ...prev, [replyTargetRootId]: true }));
  }, [replyTargetRootId]);

  useEffect(() => {
    const prev = prevReplyTargetRef.current;
    prevReplyTargetRef.current = replyTargetRootId;
    if (prev !== null && replyTargetRootId === null) {
      setRepliesExpanded({});
      setBodyCompactSignal((n) => n + 1);
    }
  }, [replyTargetRootId]);

  const selectedThread = useMemo(() => {
    if (!replyTargetRootId) return undefined;
    return visibleThreads.find(({ comment }) => comment.comment_id === replyTargetRootId);
  }, [replyTargetRootId, visibleThreads]);

  const handleFooterReply = async () => {
    if (!replyTargetRootId) return;
    const body = replyDraft.trim();
    if (!body) return;
    try {
      await onCreateReply(replyTargetRootId, body);
      // Clear only after the reply posts, so a failed send keeps the draft.
      setReplyDraft("");
      markThreadReadNow(replyTargetRootId);
    } catch {
      // Keep the draft in place so the user can retry without retyping.
    }
  };

  const scopeLabel =
    commentsScope === "project"
      ? "project"
      : commentsScope === "schedule"
        ? "schedule"
        : "screenplay";

  useEffect(() => {
    if (!pendingRootAnchor) {
      setRootDraft("");
    }
  }, [pendingRootAnchor]);

  const handleFooterRootComment = async () => {
    if (!pendingRootAnchor || !onCreateRootComment) return;
    const body = rootDraft.trim();
    if (!body) return;
    try {
      await onCreateRootComment(body);
      // Clear only after the comment posts, so a failed send keeps the draft.
      setRootDraft("");
      onClearPendingRootAnchor?.();
    } catch {
      // Keep the draft in place so the user can retry without retyping.
    }
  };

  const replyDisabled =
    readOnly || !canComment || !replyTargetRootId || !replyDraft.trim() || isCreatingComment;

  const rootComposeDisabled =
    readOnly || !canComment || !pendingRootAnchor || !rootDraft.trim() || isCreatingComment;

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Could not copy");
    }
  };

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (replyTargetRootId == null) return;
      const { target } = event;
      if (!(target instanceof Node)) return;

      if (target instanceof Element) {
        if (target.closest('[data-slot="dropdown-menu-content"]')) return;
        if (target.closest('[data-slot="dropdown-menu-sub-content"]')) return;
      }

      if (replyFooterRef.current?.contains(target)) return;

      const threadEl =
        target instanceof Element ? target.closest("[data-comment-thread-root-id]") : null;
      if (threadEl) {
        const id = threadEl.getAttribute("data-comment-thread-root-id");
        if (id === replyTargetRootId) return;
        return;
      }

      setReplyTargetRootId(null);
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [replyTargetRootId]);

  return (
    <div
      data-script-comments-panel=""
      className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden bg-editor-panel"
    >
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-3 py-2.5">
        <span className="text-xs tabular-nums text-muted-foreground">
          {visibleThreads.length} {visibleThreads.length === 1 ? "thread" : "threads"}
        </span>
        <div className="flex shrink-0 items-center gap-1">
          <ScriptCommentsPanelSortMenu
            sort={commentSort}
            onSortChange={setCommentSort}
            readStatusUnavailable={!readTimesKnown}
          />
          <ScriptCommentsPanelFilterMenu
            filters={commentFilters}
            onFiltersChange={setCommentFilters}
            showFeatureFilter={commentsScope === "project"}
            readFilterUnavailable={!readTimesKnown}
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-3 pt-3">
        {previousVersionComments ? (
          <p
            data-testid="previous-version-comments"
            className="mb-2 rounded-md border border-border bg-muted/40 px-2.5 py-1.5 text-[12px] text-muted-foreground"
          >
            {previousVersionComments}. Comments stay on the version they were written on.
          </p>
        ) : null}
        {readStateLoadFailed && !(listLoadFailed && visibleThreads.length === 0) ? (
          <QueryLoadError
            layout="inline"
            className="mb-2"
            message="Couldn't load read status"
            onRetry={() => void threadReadsQuery.refetch()}
            isRetrying={threadReadsQuery.isFetching}
          />
        ) : null}
        {readFilterPaused ? (
          <p
            role="status"
            className="mb-2 rounded-md border border-border bg-muted/40 px-2.5 py-1.5 text-[12px] text-muted-foreground"
          >
            Read filter paused until read status loads. Showing all threads.
          </p>
        ) : null}
        {listLoadFailed && visibleThreads.length > 0 ? (
          <div
            role="status"
            className="mb-2 flex items-center justify-between gap-2 rounded-md border border-border bg-muted/40 px-2.5 py-1.5 text-[12px] text-muted-foreground"
          >
            <span>Could not refresh comments. Showing the last loaded list.</span>
            {onRetryListLoad ? (
              <Button
                type="button"
                color="gray"
                size="xs"
                className="shrink-0"
                onClick={onRetryListLoad}
              >
                Retry
              </Button>
            ) : null}
          </div>
        ) : null}
        {listLoadFailed && visibleThreads.length === 0 ? (
          <div role="alert" className="flex flex-col items-center gap-3 py-12 text-center">
            <p className="text-sm text-muted-foreground">Comments could not be loaded.</p>
            {onRetryListLoad ? (
              <Button type="button" color="gray" size="sm" onClick={onRetryListLoad}>
                Try again
              </Button>
            ) : null}
          </div>
        ) : visibleThreads.length === 0 ? (
          <p className="py-12 text-center text-sm text-muted-foreground">
            {commentPanelEmptyStateMessage(appliedFilters, scopeLabel)}
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {visibleThreads.map(({ comment, replies }, index) => {
              const sortedReplies = [...replies].sort(
                (a, b) =>
                  parseInstantFromApi(a.created_at).getTime() -
                  parseInstantFromApi(b.created_at).getTime()
              );
              const threadIndex = index + 1;
              const isReplyTarget = replyTargetRootId === comment.comment_id;
              const participantIds = uniqueParticipantIds(comment, sortedReplies);
              const replyCount = sortedReplies.length;
              const repliesOpen = Boolean(repliesExpanded[comment.comment_id]);
              const primaryAnchor = comment.anchors?.[0];
              const anchorSceneId = primaryAnchor?.scene_id ?? null;
              const anchorParagraphId =
                primaryAnchor?.paragraph_id != null && Number.isFinite(primaryAnchor.paragraph_id)
                  ? primaryAnchor.paragraph_id
                  : null;
              const canGoToScreenplayAnchor =
                Boolean(onGoToAnchor) &&
                anchorParagraphId != null &&
                (primaryAnchor?.feature == null || primaryAnchor.feature === "screenplay");
              const scheduleTargetId = primaryAnchor?.target_id?.trim() ?? "";
              const threadDomainLabel =
                commentsScope === "project"
                  ? primaryAnchor?.feature === "schedule"
                    ? "Schedule"
                    : "Script"
                  : null;
              const canGoToScheduleAnchor =
                Boolean(onGoToScheduleAnchor) &&
                primaryAnchor?.feature === "schedule" &&
                scheduleTargetId.length > 0;
              const { canEdit: showOwnerThreadActions, canDelete: showThreadDelete } =
                commentActionVisibility({
                  isAuthor: isCommentOwner(comment.created_by_user_id, currentUserId),
                  canComment,
                  readOnly,
                  canModerate,
                });

              const threadUnread =
                readAtByRoot !== null &&
                isThreadUnreadByReadAt(
                  comment,
                  sortedReplies,
                  readAtByRoot[comment.comment_id],
                  currentUserId
                );

              return (
                <article
                  key={comment.comment_id}
                  data-comment-thread-root-id={comment.comment_id}
                  data-selected={isReplyTarget ? "true" : undefined}
                  className={cn(
                    "rounded-lg cursor-pointer py-3 transition-colors duration-150",
                    !isReplyTarget && "hover:bg-muted",
                    isReplyTarget &&
                      "bg-hue-sky/[0.06] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)] ring-1 ring-inset ring-hue-sky/25 hover:bg-hue-sky/10"
                  )}
                  onClick={() => engageThread(comment.comment_id)}
                >
                  <div className="flex flex-col gap-1.5 px-2.5">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center justify-between gap-2">
                        <AvatarStack
                          userIds={participantIds}
                          currentUserId={currentUserId}
                          myInitials={myInitials}
                        />
                        <div className="flex shrink-0 items-center gap-1">
                          <span
                            className="flex w-4 shrink-0 justify-center"
                            aria-hidden={!threadUnread}
                          >
                            {threadUnread ? (
                              <span
                                className="h-1.5 w-1.5 shrink-0 rounded-full bg-notification"
                                aria-label="Unread"
                                title="Unread"
                              />
                            ) : null}
                          </span>
                          {comment.status === "open" ? (
                            <span
                              className="inline-flex text-muted-foreground"
                              title="Unresolved — use menu to resolve"
                              aria-label="Unresolved thread"
                            >
                              <CheckCircle2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                            </span>
                          ) : (
                            <span
                              className="inline-flex text-success"
                              title="Resolved"
                              aria-label="Resolved thread"
                            >
                              <CheckCircle2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                            </span>
                          )}
                          <DropdownMenu modal={false}>
                            <DropdownMenuTrigger asChild>
                              <button
                                type="button"
                                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground/70 transition hover:bg-muted hover:text-foreground"
                                aria-label="Comment actions"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MoreHorizontal className="h-3.5 w-3.5" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              className="min-w-[11rem]"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {canGoToScheduleAnchor && onGoToScheduleAnchor ? (
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onGoToScheduleAnchor(scheduleTargetId);
                                  }}
                                >
                                  <LocateFixed className="mr-2 h-3.5 w-3.5 opacity-70" />
                                  Go to in schedule
                                </DropdownMenuItem>
                              ) : null}
                              {canGoToScheduleAnchor ? (
                                <DropdownMenuSeparator className="bg-muted/70" />
                              ) : null}
                              {canGoToScreenplayAnchor && onGoToAnchor ? (
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (anchorParagraphId == null) return;
                                    onGoToAnchor(anchorParagraphId, primaryAnchor?.scene_id);
                                  }}
                                >
                                  <LocateFixed className="mr-2 h-3.5 w-3.5 opacity-70" />
                                  Go to anchor in script
                                </DropdownMenuItem>
                              ) : null}
                              {canGoToScreenplayAnchor ? (
                                <DropdownMenuSeparator className="bg-muted/70" />
                              ) : null}
                              <DropdownMenuItem
                                onClick={() => void copyToClipboard(comment.body, "Comment")}
                              >
                                <Copy className="mr-2 h-3.5 w-3.5 opacity-70" />
                                Copy comment
                              </DropdownMenuItem>
                              {comment.anchors?.[0]?.anchor_text ? (
                                <DropdownMenuItem
                                  onClick={() =>
                                    void copyToClipboard(
                                      comment.anchors?.[0]?.anchor_text ?? "",
                                      "Quote"
                                    )
                                  }
                                >
                                  <Copy className="mr-2 h-3.5 w-3.5 opacity-70" />
                                  Copy anchor quote
                                </DropdownMenuItem>
                              ) : null}
                              {commentsReadsEnabled && readTimesKnown ? (
                                <>
                                  <DropdownMenuSeparator className="bg-muted/70" />
                                  <DropdownMenuItem
                                    disabled={
                                      (markCommentThreadRead.isPending &&
                                        markCommentThreadRead.variables?.rootCommentId ===
                                          comment.comment_id) ||
                                      (clearCommentThreadRead.isPending &&
                                        clearCommentThreadRead.variables?.rootCommentId ===
                                          comment.comment_id)
                                    }
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (threadUnread) {
                                        markCommentThreadRead.mutate({
                                          projectId,
                                          documentId,
                                          rootCommentId: comment.comment_id,
                                        });
                                      } else {
                                        clearCommentThreadRead.mutate({
                                          projectId,
                                          documentId,
                                          rootCommentId: comment.comment_id,
                                        });
                                      }
                                    }}
                                  >
                                    {threadUnread ? (
                                      <>
                                        <Eye className="mr-2 h-3.5 w-3.5 opacity-70" />
                                        Mark as read
                                      </>
                                    ) : (
                                      <>
                                        <EyeOff className="mr-2 h-3.5 w-3.5 opacity-70" />
                                        Mark as unread
                                      </>
                                    )}
                                  </DropdownMenuItem>
                                </>
                              ) : null}
                              {showOwnerThreadActions || showThreadDelete ? (
                                <>
                                  <DropdownMenuSeparator className="bg-muted/70" />
                                  {showOwnerThreadActions ? (
                                    <DropdownMenuItem
                                      disabled={commentMutationBusy}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingCommentId(comment.comment_id);
                                        setEditDraft(comment.body);
                                      }}
                                    >
                                      <Pencil className="mr-2 h-3.5 w-3.5 opacity-70" />
                                      Edit comment
                                    </DropdownMenuItem>
                                  ) : null}
                                  {showThreadDelete ? (
                                    <DropdownMenuItem
                                      variant="destructive"
                                      disabled={commentMutationBusy}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onDeleteComment(comment.comment_id);
                                      }}
                                    >
                                      <Trash2 className="mr-2 h-3.5 w-3.5 opacity-70" />
                                      Delete thread
                                    </DropdownMenuItem>
                                  ) : null}
                                </>
                              ) : null}
                              {canResolve && !readOnly ? (
                                <>
                                  <DropdownMenuSeparator className="bg-muted/70" />
                                  {comment.status === "open" ? (
                                    <DropdownMenuItem
                                      disabled={isResolvingComment}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        void onResolveComment(comment.comment_id);
                                      }}
                                    >
                                      <CheckCircle2 className="h-3.5 w-3.5 opacity-70" />
                                      Resolve thread
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem
                                      disabled={isReopeningComment}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        void onReopenComment(comment.comment_id);
                                      }}
                                    >
                                      <RotateCcw className="h-3.5 w-3.5 opacity-70" />
                                      Reopen thread
                                    </DropdownMenuItem>
                                  )}
                                </>
                              ) : null}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                      <p className="text-[12px] leading-tight">
                        <span className="font-semibold text-foreground">
                          {commentAuthorLabel(comment, currentUserId)}
                        </span>
                        <span className="text-muted-foreground/50"> · </span>
                        <span className="font-normal text-muted-foreground">
                          <time
                            dateTime={comment.created_at}
                            title={formatFullTimestampForTooltip(comment.created_at)}
                            className="tabular-nums"
                          >
                            {formatRelativeActivityDate(comment.created_at)}
                          </time>
                        </span>
                        <span className="text-muted-foreground/50"> · </span>
                        {threadDomainLabel ? (
                          <>
                            <span className="rounded bg-muted/50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                              {threadDomainLabel}
                            </span>
                            <span className="text-muted-foreground/50"> · </span>
                          </>
                        ) : null}
                        <ThreadMetaParts
                          sceneSummaries={sceneSummaries}
                          activeScene={activeScene}
                          anchorSceneId={anchorSceneId}
                          threadIndex={threadIndex}
                          anchorParagraphId={anchorParagraphId}
                        />
                      </p>
                    </div>

                    <div className="flex flex-col gap-1">
                      {editingCommentId === comment.comment_id ? (
                        <div
                          className="mt-0 flex flex-col gap-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <textarea
                            ref={assignEditAutosizeRef}
                            value={editDraft}
                            onChange={(e) => setEditDraft(e.target.value)}
                            disabled={isUpdatingComment}
                            rows={1}
                            maxLength={4000}
                            className={cn(replyComposerTextareaClassName, "w-full")}
                          />
                          <div className="flex justify-end gap-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="xs"
                              disabled={isUpdatingComment}
                              onClick={() => {
                                setEditingCommentId(null);
                                setEditDraft("");
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              size="xs"
                              disabled={!editDraft.trim() || isUpdatingComment}
                              onClick={() => {
                                void (async () => {
                                  try {
                                    await onUpdateComment(comment.comment_id, editDraft);
                                    // Close the editor only after the edit saves, so a failure keeps the draft.
                                    setEditingCommentId(null);
                                    setEditDraft("");
                                  } catch {
                                    // Keep the editor open with the draft so the user can retry.
                                  }
                                })();
                              }}
                            >
                              Save
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <ThreadBodyClamp
                          body={comment.body}
                          anchorText={comment.anchors?.[0]?.anchor_text ?? null}
                          variant="root"
                          compactSignal={bodyCompactSignal}
                        />
                      )}

                      {editingCommentId !== comment.comment_id ? (
                        replyCount > 0 ? (
                          <div className="pt-0.5">
                            {!repliesOpen ? (
                              <button
                                type="button"
                                aria-expanded={repliesOpen}
                                className="text-left text-[11px] font-medium text-hue-sky transition hover:text-hue-sky/80"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setRepliesExpanded((prev) => ({
                                    ...prev,
                                    [comment.comment_id]: true,
                                  }));
                                }}
                              >
                                {replyCount} {replyCount === 1 ? "reply" : "replies"}
                              </button>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  aria-expanded={repliesOpen}
                                  className="mb-1.5 text-left text-[11px] font-medium text-hue-sky transition hover:text-hue-sky/80"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setRepliesExpanded((prev) => ({
                                      ...prev,
                                      [comment.comment_id]: false,
                                    }));
                                  }}
                                >
                                  Hide {replyCount === 1 ? "reply" : "replies"}
                                </button>
                                <div className="flex flex-col gap-2 border-l border-border pl-3">
                                  {sortedReplies.map((reply) => {
                                    const replyAnchorText = reply.anchors?.[0]?.anchor_text ?? null;
                                    const {
                                      canEdit: showOwnerReplyActions,
                                      canDelete: showReplyDelete,
                                    } = commentActionVisibility({
                                      isAuthor: isCommentOwner(
                                        reply.created_by_user_id,
                                        currentUserId
                                      ),
                                      canComment,
                                      readOnly,
                                      canModerate,
                                    });
                                    return (
                                      <div key={reply.comment_id} className="flex flex-col gap-1">
                                        <div className="flex items-start gap-2">
                                          <SmallAvatar
                                            userId={reply.created_by_user_id}
                                            currentUserId={currentUserId}
                                            myInitials={myInitials}
                                          />
                                          <div className="flex min-w-0 flex-1 items-start justify-between gap-1">
                                            <p className="min-w-0 flex-1 text-[11px] leading-tight">
                                              <span className="font-semibold text-foreground">
                                                {commentAuthorLabel(reply, currentUserId)}
                                              </span>
                                              <span className="text-muted-foreground/50"> · </span>
                                              <span className="font-normal text-muted-foreground">
                                                <time
                                                  dateTime={reply.created_at}
                                                  title={formatFullTimestampForTooltip(
                                                    reply.created_at
                                                  )}
                                                  className="tabular-nums"
                                                >
                                                  {formatRelativeActivityDate(reply.created_at)}
                                                </time>
                                              </span>
                                            </p>
                                            <DropdownMenu modal={false}>
                                              <DropdownMenuTrigger asChild>
                                                <button
                                                  type="button"
                                                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground/70 transition hover:bg-muted hover:text-foreground"
                                                  aria-label="Reply actions"
                                                  onClick={(e) => e.stopPropagation()}
                                                >
                                                  <MoreHorizontal className="h-3.5 w-3.5" />
                                                </button>
                                              </DropdownMenuTrigger>
                                              <DropdownMenuContent
                                                align="end"
                                                className="min-w-[11rem]"
                                                onClick={(e) => e.stopPropagation()}
                                              >
                                                <DropdownMenuItem
                                                  onClick={() =>
                                                    void copyToClipboard(reply.body, "Comment")
                                                  }
                                                >
                                                  <Copy className="mr-2 h-3.5 w-3.5 opacity-70" />
                                                  Copy comment
                                                </DropdownMenuItem>
                                                {replyAnchorText ? (
                                                  <DropdownMenuItem
                                                    onClick={() =>
                                                      void copyToClipboard(replyAnchorText, "Quote")
                                                    }
                                                  >
                                                    <Copy className="mr-2 h-3.5 w-3.5 opacity-70" />
                                                    Copy anchor quote
                                                  </DropdownMenuItem>
                                                ) : null}
                                                {showOwnerReplyActions || showReplyDelete ? (
                                                  <>
                                                    <DropdownMenuSeparator className="bg-muted/70" />
                                                    {showOwnerReplyActions ? (
                                                      <DropdownMenuItem
                                                        disabled={commentMutationBusy}
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setEditingCommentId(reply.comment_id);
                                                          setEditDraft(reply.body);
                                                        }}
                                                      >
                                                        <Pencil className="mr-2 h-3.5 w-3.5 opacity-70" />
                                                        Edit reply
                                                      </DropdownMenuItem>
                                                    ) : null}
                                                    {showReplyDelete ? (
                                                      <DropdownMenuItem
                                                        variant="destructive"
                                                        disabled={commentMutationBusy}
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          onDeleteComment(reply.comment_id);
                                                        }}
                                                      >
                                                        <Trash2 className="mr-2 h-3.5 w-3.5 opacity-70" />
                                                        Delete reply
                                                      </DropdownMenuItem>
                                                    ) : null}
                                                  </>
                                                ) : null}
                                              </DropdownMenuContent>
                                            </DropdownMenu>
                                          </div>
                                        </div>
                                        <div className="min-w-0 w-full pl-9">
                                          {editingCommentId === reply.comment_id ? (
                                            <div
                                              className="flex flex-col gap-2"
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              <textarea
                                                ref={assignEditAutosizeRef}
                                                value={editDraft}
                                                onChange={(e) => setEditDraft(e.target.value)}
                                                disabled={isUpdatingComment}
                                                rows={1}
                                                maxLength={4000}
                                                className={cn(
                                                  replyComposerTextareaClassName,
                                                  "w-full"
                                                )}
                                              />
                                              <div className="flex justify-end gap-2">
                                                <Button
                                                  type="button"
                                                  variant="ghost"
                                                  size="xs"
                                                  disabled={isUpdatingComment}
                                                  onClick={() => {
                                                    setEditingCommentId(null);
                                                    setEditDraft("");
                                                  }}
                                                >
                                                  Cancel
                                                </Button>
                                                <Button
                                                  type="button"
                                                  size="xs"
                                                  disabled={!editDraft.trim() || isUpdatingComment}
                                                  onClick={() => {
                                                    void (async () => {
                                                      try {
                                                        await onUpdateComment(
                                                          reply.comment_id,
                                                          editDraft
                                                        );
                                                        // Close the editor only after the edit saves, so a failure keeps the draft.
                                                        setEditingCommentId(null);
                                                        setEditDraft("");
                                                      } catch {
                                                        // Keep the editor open with the draft so the user can retry.
                                                      }
                                                    })();
                                                  }}
                                                >
                                                  Save
                                                </Button>
                                              </div>
                                            </div>
                                          ) : (
                                            <ThreadBodyClamp
                                              variant="reply"
                                              body={reply.body}
                                              anchorText={null}
                                              compactSignal={bodyCompactSignal}
                                            />
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </>
                            )}
                          </div>
                        ) : (
                          <p className="pt-0.5 text-[11px] tabular-nums text-muted-foreground/50">
                            0 replies
                          </p>
                        )
                      ) : null}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      <div
        ref={replyFooterRef}
        data-script-comments-reply-footer=""
        className="shrink-0 border-t border-border bg-card/95 px-3 py-2.5 backdrop-blur-md"
      >
        <div className="mb-2 flex min-h-[1.25rem] items-center justify-between gap-2">
          {pendingRootAnchor ? (
            <>
              <p className="min-w-0 flex-1 text-[11px] text-muted-foreground">
                <span className="text-muted-foreground">Comment on</span>{" "}
                <span className="text-foreground/80">{pendingRootAnchor.anchorText}</span>
              </p>
              {onClearPendingRootAnchor ? (
                <Button
                  type="button"
                  color="gray"
                  size="icon"
                  className="h-7 w-7 shrink-0"
                  aria-label="Cancel new comment"
                  onClick={() => onClearPendingRootAnchor()}
                >
                  <X className="h-4 w-4" />
                </Button>
              ) : null}
            </>
          ) : selectedThread ? (
            <>
              <p className="min-w-0 flex-1 text-[11px] text-muted-foreground">
                <span className="text-muted-foreground">
                  {canComment && !readOnly ? "Replying to" : "Viewing"}
                </span>{" "}
                <ThreadMetaParts
                  sceneSummaries={sceneSummaries}
                  activeScene={activeScene}
                  anchorSceneId={selectedThread.comment.anchors?.[0]?.scene_id ?? null}
                  threadIndex={
                    visibleThreads.findIndex(
                      (t) => t.comment.comment_id === selectedThread.comment.comment_id
                    ) + 1
                  }
                  anchorParagraphId={selectedThread.comment.anchors?.[0]?.paragraph_id ?? null}
                />
                <span className="text-muted-foreground/70"> · </span>
                <span className="italic text-muted-foreground">
                  &quot;{truncatePreview(selectedThread.comment.body, 42)}&quot;
                </span>
              </p>
              <Button
                type="button"
                color="gray"
                size="icon"
                className="h-7 w-7 shrink-0"
                aria-label="Clear reply target"
                onClick={() => setReplyTargetRootId(null)}
              >
                <X className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <p className="text-[11px] text-muted-foreground/70">
              {canComment && !readOnly
                ? "Select a comment above, then write a reply."
                : "Replies are disabled."}
            </p>
          )}
        </div>
        <div className="flex items-end gap-2">
          <textarea
            ref={assignReplyAutosizeRef}
            value={pendingRootAnchor ? rootDraft : replyDraft}
            onChange={(event) => {
              if (pendingRootAnchor) {
                setRootDraft(event.target.value);
              } else {
                setReplyDraft(event.target.value);
              }
            }}
            placeholder={
              pendingRootAnchor && canComment && !readOnly
                ? "Write a comment…"
                : replyTargetRootId && canComment && !readOnly
                  ? "Write a reply…"
                  : canComment && !readOnly
                    ? "Choose a thread to reply…"
                    : "Replies unavailable"
            }
            disabled={
              pendingRootAnchor
                ? readOnly || !canComment
                : readOnly || !canComment || !replyTargetRootId
            }
            rows={1}
            maxLength={4000}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (pendingRootAnchor) {
                  if (!rootComposeDisabled) {
                    void handleFooterRootComment();
                  }
                } else if (!replyDisabled) {
                  void handleFooterReply();
                }
              }
            }}
            className={cn(replyComposerTextareaClassName, "flex-1")}
          />
          <Button
            type="button"
            size="icon"
            disabled={pendingRootAnchor ? rootComposeDisabled : replyDisabled}
            aria-label={pendingRootAnchor ? "Send comment" : "Send reply"}
            onClick={() =>
              void (pendingRootAnchor ? handleFooterRootComment() : handleFooterReply())
            }
            className="h-11 w-11 shrink-0 rounded-xl"
          >
            <Send className="h-4 w-4" aria-hidden />
          </Button>
        </div>
        {canComment && !readOnly ? (
          <p className="mt-2 text-[10px] text-muted-foreground/70">
            Enter to send · Shift+Enter for new line
          </p>
        ) : null}
      </div>
    </div>
  );
};
